import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { TrendUp, ChartBar, Target, Timer, ArrowsLeftRight, Crosshair, Percent, Prohibit, CalendarBlank, ChartLine } from "phosphor-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Brand logos
const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/r9xkm2vj_cerebro%20%2B%20genilateral.png";
const LOGO_PUREBRAIN = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/gtg4vc6a_purebrain.png";

// Interactive mode navigation tabs
const INTERACTIVE_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'performance', label: 'Performance' },
  { id: 'setups', label: 'Setups' },
  { id: 'breakdown', label: 'Breakdown' }
];

// Default config for old shares that don't have config
const DEFAULT_CONFIG = {
  sections: {
    global_performance: true,
    trade_type_comparison: true,
    execution_quality: true,
    planned_not_triggered: false,
    partial_execution: false,
    timeframe_breakdown: true,
    setup_performance: true
  }
};

// Time range display labels
const TIME_RANGE_LABELS = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  'all': 'All time'
};

// Profit Factor color based on value
const getProfitFactorColor = (pf) => {
  const pfValue = parseFloat(pf) || 0;
  if (pfValue < 1.0) return "text-[#FF3B30]";
  if (pfValue <= 1.5) return "text-[#FFC107]";
  return "text-[#00E676]";
};

const StatCard = ({ title, value, subtitle, icon: Icon, highlight = false }) => (
  <div className="bg-[#121212] border border-white/10 rounded-sm p-5">
    <div className="flex items-start justify-between mb-2">
      <div className="text-white/40 text-xs font-mono uppercase tracking-wider">{title}</div>
      {Icon && <Icon size={18} weight="duotone" className="text-white/40" />}
    </div>
    <div className={`text-2xl font-bold font-mono mb-1 ${highlight ? highlight : 'text-white'}`}>
      {value}
    </div>
    {subtitle && <div className="text-white/50 text-sm">{subtitle}</div>}
  </div>
);

const BreakdownTable = ({ title, data, columns }) => (
  <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
    <div className="px-5 py-4 border-b border-white/10">
      <h3 className="text-base font-bold font-heading">{title}</h3>
    </div>
    {data.length === 0 ? (
      <div className="p-5 text-white/40 text-sm">No data available</div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-[#0A0A0A]">
            <tr>
              {columns.map((col, i) => (
                <th key={i} className="px-4 py-3 text-left text-xs font-mono text-white/40 uppercase">
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {data.map((row, i) => (
              <tr key={i} className="hover:bg-white/5 transition-colors">
                {columns.map((col, j) => (
                  <td key={j} className={`px-4 py-3 text-sm font-mono ${col.className || ''}`}>
                    {col.render ? col.render(row) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

export default function ShareView() {
  const { shareId } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  
  // Interactive mode state
  const mode = searchParams.get('mode') || 'simple';
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const fetchShareData = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/public/share/${shareId}`);
        setData(response.data);
      } catch (err) {
        console.error("Error fetching share data:", err);
        if (err.response?.status === 404) {
          setError("This share link is invalid or has expired.");
        } else {
          setError("Failed to load performance data. Please try again later.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchShareData();
  }, [shareId]);

  // Get config with fallback for old shares
  const config = useMemo(() => {
    if (!data?.share_info?.config) {
      return DEFAULT_CONFIG;
    }
    return {
      sections: { ...DEFAULT_CONFIG.sections, ...data.share_info.config.sections }
    };
  }, [data]);

  // Get time range with fallback
  const timeRange = data?.share_info?.time_range || 'all';
  
  // Check if this is an imported analytics share
  const isImportedShare = data?.share_info?.share_type === 'imported_analytics';

  // Check if section is enabled
  const isSectionEnabled = (sectionKey) => {
    return config.sections[sectionKey] ?? true;
  };

  // Render imported analytics share view
  if (!loading && !error && isImportedShare) {
    const importedContext = data?.share_info?.imported_context || {};
    const globalAnalytics = data?.global_analytics || {};
    const setupAnalytics = data?.setup_analytics || [];
    const timeframeAnalytics = data?.timeframe_analytics || [];
    const batchComparison = data?.batch_comparison || [];
    
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        {/* Header */}
        <div className="bg-[#121212] border-b border-white/10">
          <div className="max-w-5xl mx-auto px-6 py-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#00B8D4]/10 rounded-full flex items-center justify-center">
                <ChartBar size={20} className="text-[#00B8D4]" />
              </div>
              <div>
                <h1 className="text-xl font-bold font-heading">{data?.share_info?.title || 'Imported Analytics Report'}</h1>
                {data?.share_info?.description && (
                  <p className="text-white/50 text-sm">{data.share_info.description}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-4 text-xs text-white/40">
              <span className="bg-[#00B8D4]/10 text-[#00B8D4] px-2 py-1 rounded-sm">
                Imported Analytics
              </span>
              {importedContext.compare_mode && (
                <span className="bg-[#FFC107]/10 text-[#FFC107] px-2 py-1 rounded-sm">
                  Batch Comparison
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="max-w-5xl mx-auto px-6 py-8 space-y-8">
          {/* Global Performance */}
          {isSectionEnabled('imported_global_performance') && (
            <div>
              <h2 className="text-lg font-bold font-heading mb-4">Global Performance</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard 
                  title="Total Trades" 
                  value={globalAnalytics.total_trades || 0}
                  icon={ChartBar}
                />
                <StatCard 
                  title="Win Rate" 
                  value={`${globalAnalytics.win_rate || 0}%`}
                  subtitle={`${globalAnalytics.wins || 0}W / ${globalAnalytics.losses || 0}L`}
                  icon={TrendUp}
                  highlight={globalAnalytics.win_rate >= 50 ? 'text-[#00E676]' : 'text-[#FF3B30]'}
                />
                <StatCard 
                  title="Avg RR" 
                  value={`${globalAnalytics.avg_rr || 0}R`}
                  icon={Target}
                  highlight={globalAnalytics.avg_rr >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'}
                />
                <StatCard 
                  title="Profit Factor" 
                  value={globalAnalytics.profit_factor || 'N/A'}
                  icon={ChartLine}
                  highlight={getProfitFactorColor(globalAnalytics.profit_factor)}
                />
              </div>
            </div>
          )}
          
          {/* Batch Comparison */}
          {isSectionEnabled('batch_comparison') && batchComparison.length >= 2 && (
            <div>
              <h2 className="text-lg font-bold font-heading mb-4">Batch Comparison</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {batchComparison.map((batch, index) => {
                  const colors = ['#00E676', '#00B8D4', '#FFC107', '#FF6B6B'];
                  return (
                    <div 
                      key={batch.batch_id}
                      className="bg-[#121212] border rounded-sm p-4"
                      style={{ borderColor: colors[index] }}
                    >
                      <div 
                        className="text-sm font-medium mb-3 pb-2 border-b border-white/10"
                        style={{ color: colors[index] }}
                      >
                        {batch.import_name}
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-white/50">Trades</span>
                          <span className="text-white font-medium">{batch.total_trades}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white/50">Win Rate</span>
                          <span className={batch.win_rate >= 50 ? 'text-[#00E676]' : 'text-[#FF3B30]'}>
                            {batch.win_rate}%
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white/50">Avg RR</span>
                          <span className={batch.avg_rr >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'}>
                            {batch.avg_rr}R
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white/50">Profit Factor</span>
                          <span className={getProfitFactorColor(batch.profit_factor)}>
                            {batch.profit_factor || 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Setup Performance */}
          {isSectionEnabled('imported_setup_performance') && setupAnalytics.length > 0 && (
            <BreakdownTable
              title="Setup Performance"
              data={setupAnalytics}
              columns={[
                { key: 'setup_name', label: 'Setup' },
                { key: 'total_trades', label: 'Trades', align: 'right' },
                { key: 'win_rate', label: 'Win Rate', align: 'right', format: (v) => `${v}%`, color: (row) => row.win_rate >= 50 ? 'text-[#00E676]' : 'text-[#FF3B30]' },
                { key: 'avg_rr', label: 'Avg RR', align: 'right', format: (v) => `${v}R`, color: (row) => row.avg_rr >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]' }
              ]}
            />
          )}
          
          {/* Timeframe Performance */}
          {isSectionEnabled('imported_timeframe_performance') && timeframeAnalytics.length > 0 && (
            <BreakdownTable
              title="Timeframe Performance"
              data={timeframeAnalytics}
              columns={[
                { key: 'timeframe', label: 'Timeframe' },
                { key: 'total_trades', label: 'Trades', align: 'right' },
                { key: 'win_rate', label: 'Win Rate', align: 'right', format: (v) => `${v}%`, color: (row) => row.win_rate >= 50 ? 'text-[#00E676]' : 'text-[#FF3B30]' },
                { key: 'avg_rr', label: 'Avg RR', align: 'right', format: (v) => `${v}R`, color: (row) => row.avg_rr >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]' }
              ]}
            />
          )}
          
          {/* Footer */}
          <div className="text-center pt-8 border-t border-white/10">
            <p className="text-white/40 text-sm">
              Imported Analytics shared via{' '}
              <Link to="/" className="text-[#00B8D4] hover:underline">GENI</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-white/60 animate-pulse font-mono">Loading performance data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center gap-4">
        <div className="text-[#FF3B30] text-lg">{error}</div>
        <Link 
          to="/" 
          className="text-white/60 hover:text-white text-sm underline"
        >
          Go to GENI
        </Link>
      </div>
    );
  }

  const { share_info, global_analytics, setup_analytics, asset_group_analytics, trade_type_breakdown, timeframe_breakdown, planned_trades_analytics, partial_execution_analytics } = data;

  // Count enabled sections
  const enabledSectionsCount = Object.values(config.sections).filter(Boolean).length;
  
  // If no sections are enabled, show a message
  if (enabledSectionsCount === 0) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center gap-4">
        <div className="text-white/60 text-lg">No analytics sections are shared in this report.</div>
        <Link 
          to="/" 
          className="text-white/60 hover:text-white text-sm underline"
        >
          Go to GENI
        </Link>
      </div>
    );
  }

  const setupColumns = [
    { key: 'setup_name', label: 'Setup' },
    { key: 'total_trades', label: 'Trades' },
    { 
      key: 'win_rate', 
      label: 'Win Rate',
      render: (row) => (
        <span className={row.win_rate >= 50 ? 'text-[#00E676]' : 'text-[#FFC107]'}>
          {row.win_rate}%
        </span>
      )
    },
    { 
      key: 'avg_loss_rr', 
      label: 'Avg Loss RR',
      render: (row) => (
        <span>
          {row.avg_loss_rr !== null ? `${row.avg_loss_rr}R` : '—'}
        </span>
      )
    },
    { 
      key: 'profit_factor', 
      label: 'Profit Factor',
      render: (row) => (
        <span className={getProfitFactorColor(row.profit_factor)}>
          {row.profit_factor}
        </span>
      )
    },
  ];

  const groupColumns = [
    { key: 'asset_group_name', label: 'Asset Group' },
    { key: 'total_trades', label: 'Trades' },
    { 
      key: 'win_rate', 
      label: 'Win Rate',
      render: (row) => (
        <span className={row.win_rate >= 50 ? 'text-[#00E676]' : 'text-[#FFC107]'}>
          {row.win_rate}%
        </span>
      )
    },
    { key: 'average_rr', label: 'Avg RR' },
    { 
      key: 'profit_factor', 
      label: 'Profit Factor',
      render: (row) => (
        <span className={getProfitFactorColor(row.profit_factor)}>
          {row.profit_factor}
        </span>
      )
    },
  ];

  const timeframeColumns = [
    { key: 'timeframe', label: 'Timeframe' },
    { key: 'total_trades', label: 'Trades' },
    { 
      key: 'win_rate', 
      label: 'Win Rate',
      render: (row) => (
        <span className={row.win_rate >= 50 ? 'text-[#00E676]' : 'text-[#FFC107]'}>
          {row.win_rate}%
        </span>
      )
    },
    { key: 'average_rr', label: 'Avg RR' },
    { 
      key: 'profit_factor', 
      label: 'Profit Factor',
      render: (row) => (
        <span className={getProfitFactorColor(row.profit_factor)}>
          {row.profit_factor}
        </span>
      )
    },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#0A0A0A]">
        <div className="w-full max-w-[1200px] mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center">
            <img
              src={LOGO_HORIZONTAL}
              alt="GENI"
              className="h-10 md:h-12 lg:h-14 w-auto object-contain"
            />
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="w-full max-w-[1200px] mx-auto px-4 md:px-8 py-8">
        {/* Title */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-black font-heading tracking-tight mb-2">
            {share_info.title}
          </h1>
          {share_info.description && (
            <p className="text-white/60 text-base mb-3">{share_info.description}</p>
          )}
          <div className="flex items-center gap-4 flex-wrap">
            <p className="text-white/40 text-sm font-mono">
              Shared on {new Date(share_info.created_at).toLocaleDateString()}
            </p>
            {timeRange !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#00E676]/10 border border-[#00E676]/30 rounded-sm text-xs font-mono text-[#00E676]">
                <CalendarBlank size={12} weight="duotone" />
                {TIME_RANGE_LABELS[timeRange] || timeRange}
              </span>
            )}
          </div>
        </div>

        {/* Interactive Mode Navigation */}
        {mode === 'interactive' && (
          <div className="flex justify-center gap-2 md:gap-4 mb-8 overflow-x-auto pb-2" data-testid="interactive-nav">
            {INTERACTIVE_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-[#00E676] text-black'
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* === SIMPLE MODE: Show all sections === */}
        {mode === 'simple' && (
          <>
            {/* Global Performance - always first if enabled */}
            {isSectionEnabled('global_performance') && (
              <div className="mb-8" data-testid="section-global-performance">
                <h2 className="text-lg font-bold font-heading mb-4 flex items-center gap-2">
                  <TrendUp size={20} weight="duotone" className="text-white/60" />
                  Overview
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatCard
                    title="Total Trades"
                    value={global_analytics.total_trades}
                    subtitle={`${global_analytics.win_count}W / ${global_analytics.loss_count}L`}
                    icon={ChartBar}
                  />
              <StatCard
                title="Win Rate"
                value={`${global_analytics.win_rate}%`}
                icon={Target}
                highlight={global_analytics.win_rate >= 50 ? "text-[#00E676]" : "text-[#FFC107]"}
              />
              <StatCard
                title="Average RR"
                value={global_analytics.average_rr}
                icon={ArrowsLeftRight}
              />
              <StatCard
                title="Profit Factor"
                value={global_analytics.profit_factor}
                subtitle={global_analytics.profit_factor_interpretation}
                icon={TrendUp}
                highlight={getProfitFactorColor(global_analytics.profit_factor)}
              />
            </div>
          </div>
        )}

        {/* Trade Type Comparison */}
        {isSectionEnabled('trade_type_comparison') && 
          (trade_type_breakdown?.day_trade?.total_trades > 0 || trade_type_breakdown?.swing_trade?.total_trades > 0) && (
          <div className="mb-8" data-testid="section-trade-type-comparison">
            <h2 className="text-lg font-bold font-heading mb-4 flex items-center gap-2">
              <Timer size={20} weight="duotone" className="text-white/60" />
              By Trade Type
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {trade_type_breakdown.day_trade?.total_trades > 0 && (
                <div className="bg-[#121212] border border-white/10 rounded-sm p-5">
                  <div className="text-white/40 text-xs font-mono uppercase mb-2">Day Trading</div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <div className="text-xl font-bold font-mono text-white">{trade_type_breakdown.day_trade.total_trades}</div>
                      <div className="text-white/40 text-xs">Trades</div>
                    </div>
                    <div>
                      <div className={`text-xl font-bold font-mono ${trade_type_breakdown.day_trade.win_rate >= 50 ? 'text-[#00E676]' : 'text-[#FFC107]'}`}>
                        {trade_type_breakdown.day_trade.win_rate}%
                      </div>
                      <div className="text-white/40 text-xs">Win Rate</div>
                    </div>
                    <div>
                      <div className={`text-xl font-bold font-mono ${getProfitFactorColor(trade_type_breakdown.day_trade.profit_factor)}`}>
                        {trade_type_breakdown.day_trade.profit_factor}
                      </div>
                      <div className="text-white/40 text-xs">PF</div>
                    </div>
                  </div>
                </div>
              )}
              {trade_type_breakdown.swing_trade?.total_trades > 0 && (
                <div className="bg-[#121212] border border-white/10 rounded-sm p-5">
                  <div className="text-white/40 text-xs font-mono uppercase mb-2">Swing Trading</div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <div className="text-xl font-bold font-mono text-white">{trade_type_breakdown.swing_trade.total_trades}</div>
                      <div className="text-white/40 text-xs">Trades</div>
                    </div>
                    <div>
                      <div className={`text-xl font-bold font-mono ${trade_type_breakdown.swing_trade.win_rate >= 50 ? 'text-[#00E676]' : 'text-[#FFC107]'}`}>
                        {trade_type_breakdown.swing_trade.win_rate}%
                      </div>
                      <div className="text-white/40 text-xs">Win Rate</div>
                    </div>
                    <div>
                      <div className={`text-xl font-bold font-mono ${getProfitFactorColor(trade_type_breakdown.swing_trade.profit_factor)}`}>
                        {trade_type_breakdown.swing_trade.profit_factor}
                      </div>
                      <div className="text-white/40 text-xs">PF</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Execution Quality - simplified view for public */}
        {isSectionEnabled('execution_quality') && global_analytics.total_trades > 0 && (
          <div className="mb-8" data-testid="section-execution-quality">
            <h2 className="text-lg font-bold font-heading mb-4 flex items-center gap-2">
              <Crosshair size={20} weight="duotone" className="text-white/60" />
              Execution Quality
            </h2>
            <div className="bg-[#121212] border border-white/10 rounded-sm p-5">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <div className="text-white/40 text-xs font-mono mb-1">TRADES ANALYZED</div>
                  <div className="text-xl font-bold font-mono text-white">{global_analytics.total_trades}</div>
                </div>
                <div>
                  <div className="text-white/40 text-xs font-mono mb-1">WIN RATE</div>
                  <div className={`text-xl font-bold font-mono ${global_analytics.win_rate >= 50 ? 'text-[#00E676]' : 'text-[#FFC107]'}`}>
                    {global_analytics.win_rate}%
                  </div>
                </div>
                <div>
                  <div className="text-white/40 text-xs font-mono mb-1">AVG LOSS RR</div>
                  <div className="text-xl font-bold font-mono text-white">
                    {global_analytics.avg_loss_rr !== null ? `${global_analytics.avg_loss_rr}R` : '—'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Planned Trades Not Triggered - only show if data exists */}
        {isSectionEnabled('planned_not_triggered') && planned_trades_analytics?.total_planned > 0 && (
          <div className="mb-8" data-testid="section-planned-not-triggered">
            <h2 className="text-lg font-bold font-heading mb-4 flex items-center gap-2">
              <Prohibit size={20} weight="duotone" className="text-[#FFA726]" />
              Planned Trades (Not Triggered)
            </h2>
            <div className="bg-[#121212] border border-white/10 rounded-sm p-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <div className="text-white/40 text-xs font-mono mb-1">TOTAL PLANNED</div>
                  <div className="text-2xl font-bold font-mono text-[#FFA726]">{planned_trades_analytics.total_planned}</div>
                </div>
                <div className="md:col-span-2">
                  <div className="text-white/40 text-xs font-mono mb-2">BY SETUP</div>
                  <div className="flex flex-wrap gap-2">
                    {planned_trades_analytics.by_setup?.slice(0, 5).map((item, idx) => (
                      <span key={idx} className="px-2 py-1 bg-white/5 border border-white/10 rounded text-sm text-white/80">
                        {item.setup}: <span className="text-[#FFA726]">{item.count}</span>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Partial Execution - only show if data exists */}
        {isSectionEnabled('partial_execution') && partial_execution_analytics?.total_partial > 0 && (
          <div className="mb-8" data-testid="section-partial-execution">
            <h2 className="text-lg font-bold font-heading mb-4 flex items-center gap-2">
              <Percent size={20} weight="duotone" className="text-white/60" />
              Partial Execution
            </h2>
            <div className="bg-[#121212] border border-white/10 rounded-sm p-5">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <div className="text-white/40 text-xs font-mono mb-1">PARTIAL CLOSES</div>
                  <div className="text-xl font-bold font-mono text-white">{partial_execution_analytics.total_partial}</div>
                </div>
                <div>
                  <div className="text-white/40 text-xs font-mono mb-1">WINS</div>
                  <div className="text-xl font-bold font-mono text-[#00E676]">{partial_execution_analytics.win_count}</div>
                </div>
                <div>
                  <div className="text-white/40 text-xs font-mono mb-1">LOSSES</div>
                  <div className="text-xl font-bold font-mono text-[#FF3B30]">{partial_execution_analytics.loss_count}</div>
                </div>
                <div>
                  <div className="text-white/40 text-xs font-mono mb-1">WIN RATE</div>
                  <div className={`text-xl font-bold font-mono ${partial_execution_analytics.win_rate >= 50 ? 'text-[#00E676]' : 'text-[#FFC107]'}`}>
                    {partial_execution_analytics.win_rate}%
                  </div>
                </div>
              </div>
              {partial_execution_analytics.by_reason?.length > 0 && (
                <div>
                  <div className="text-white/40 text-xs font-mono mb-2">BY REASON</div>
                  <div className="flex flex-wrap gap-2">
                    {partial_execution_analytics.by_reason.map((item, idx) => (
                      <span key={idx} className="px-2 py-1 bg-white/5 border border-white/10 rounded text-sm text-white/80">
                        {item.reason}: <span className="text-white">{item.count}</span> ({item.win_rate}%)
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Setup Performance */}
        {isSectionEnabled('setup_performance') && setup_analytics.length > 0 && (
          <div className="mb-8" data-testid="section-setup-performance">
            <h2 className="text-lg font-bold font-heading mb-4">Setup Performance</h2>
            <BreakdownTable 
              title="Performance by Setup" 
              data={setup_analytics.filter(s => s.total_trades > 0)} 
              columns={setupColumns} 
            />
          </div>
        )}

        {/* Asset Group Performance - shown within setup performance section */}
        {isSectionEnabled('setup_performance') && asset_group_analytics.filter(g => g.total_trades > 0).length > 0 && (
          <div className="mb-8" data-testid="section-asset-group-performance">
            <h2 className="text-lg font-bold font-heading mb-4">Asset Group Performance</h2>
            <BreakdownTable 
              title="Performance by Asset Group" 
              data={asset_group_analytics.filter(g => g.total_trades > 0)} 
              columns={groupColumns} 
            />
          </div>
        )}

        {/* Timeframe Performance */}
        {isSectionEnabled('timeframe_breakdown') && timeframe_breakdown.length > 0 && (
          <div className="mb-8" data-testid="section-timeframe-breakdown">
            <h2 className="text-lg font-bold font-heading mb-4">Timeframe Performance</h2>
            <BreakdownTable 
              title="Performance by Timeframe" 
              data={timeframe_breakdown} 
              columns={timeframeColumns} 
            />
          </div>
        )}
          </>
        )}

        {/* === INTERACTIVE MODE: Show sections based on active tab === */}
        {mode === 'interactive' && (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && isSectionEnabled('global_performance') && (
              <div className="mb-8" data-testid="interactive-overview">
                <h2 className="text-lg font-bold font-heading mb-4 flex items-center gap-2">
                  <TrendUp size={20} weight="duotone" className="text-white/60" />
                  Overview
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatCard
                    title="Total Trades"
                    value={global_analytics.total_trades}
                    subtitle={`${global_analytics.win_count}W / ${global_analytics.loss_count}L`}
                    icon={ChartBar}
                  />
                  <StatCard
                    title="Win Rate"
                    value={`${global_analytics.win_rate}%`}
                    icon={Target}
                    highlight={global_analytics.win_rate >= 50 ? "text-[#00E676]" : "text-[#FFC107]"}
                  />
                  <StatCard
                    title="Average RR"
                    value={global_analytics.average_rr}
                    icon={ArrowsLeftRight}
                  />
                  <StatCard
                    title="Profit Factor"
                    value={global_analytics.profit_factor}
                    subtitle={global_analytics.profit_factor_interpretation}
                    icon={TrendUp}
                    highlight={getProfitFactorColor(global_analytics.profit_factor)}
                  />
                </div>
              </div>
            )}

            {/* Performance Tab */}
            {activeTab === 'performance' && (
              <div data-testid="interactive-performance">
                {/* Trade Type Comparison */}
                {isSectionEnabled('trade_type_comparison') && 
                  (trade_type_breakdown?.day_trade?.total_trades > 0 || trade_type_breakdown?.swing_trade?.total_trades > 0) && (
                  <div className="mb-8">
                    <h2 className="text-lg font-bold font-heading mb-4 flex items-center gap-2">
                      <Timer size={20} weight="duotone" className="text-white/60" />
                      By Trade Type
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {trade_type_breakdown.day_trade?.total_trades > 0 && (
                        <div className="bg-[#121212] border border-white/10 rounded-sm p-5">
                          <div className="text-white/40 text-xs font-mono uppercase mb-2">Day Trading</div>
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <div className="text-xl font-bold font-mono text-white">{trade_type_breakdown.day_trade.total_trades}</div>
                              <div className="text-white/40 text-xs">Trades</div>
                            </div>
                            <div>
                              <div className={`text-xl font-bold font-mono ${trade_type_breakdown.day_trade.win_rate >= 50 ? 'text-[#00E676]' : 'text-[#FFC107]'}`}>
                                {trade_type_breakdown.day_trade.win_rate}%
                              </div>
                              <div className="text-white/40 text-xs">Win Rate</div>
                            </div>
                            <div>
                              <div className={`text-xl font-bold font-mono ${getProfitFactorColor(trade_type_breakdown.day_trade.profit_factor)}`}>
                                {trade_type_breakdown.day_trade.profit_factor}
                              </div>
                              <div className="text-white/40 text-xs">PF</div>
                            </div>
                          </div>
                        </div>
                      )}
                      {trade_type_breakdown.swing_trade?.total_trades > 0 && (
                        <div className="bg-[#121212] border border-white/10 rounded-sm p-5">
                          <div className="text-white/40 text-xs font-mono uppercase mb-2">Swing Trading</div>
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <div className="text-xl font-bold font-mono text-white">{trade_type_breakdown.swing_trade.total_trades}</div>
                              <div className="text-white/40 text-xs">Trades</div>
                            </div>
                            <div>
                              <div className={`text-xl font-bold font-mono ${trade_type_breakdown.swing_trade.win_rate >= 50 ? 'text-[#00E676]' : 'text-[#FFC107]'}`}>
                                {trade_type_breakdown.swing_trade.win_rate}%
                              </div>
                              <div className="text-white/40 text-xs">Win Rate</div>
                            </div>
                            <div>
                              <div className={`text-xl font-bold font-mono ${getProfitFactorColor(trade_type_breakdown.swing_trade.profit_factor)}`}>
                                {trade_type_breakdown.swing_trade.profit_factor}
                              </div>
                              <div className="text-white/40 text-xs">PF</div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Execution Quality */}
                {isSectionEnabled('execution_quality') && global_analytics.total_trades > 0 && (
                  <div className="mb-8">
                    <h2 className="text-lg font-bold font-heading mb-4 flex items-center gap-2">
                      <Crosshair size={20} weight="duotone" className="text-white/60" />
                      Execution Quality
                    </h2>
                    <div className="bg-[#121212] border border-white/10 rounded-sm p-5">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div>
                          <div className="text-white/40 text-xs font-mono mb-1">TRADES ANALYZED</div>
                          <div className="text-xl font-bold font-mono text-white">{global_analytics.total_trades}</div>
                        </div>
                        <div>
                          <div className="text-white/40 text-xs font-mono mb-1">WIN RATE</div>
                          <div className={`text-xl font-bold font-mono ${global_analytics.win_rate >= 50 ? 'text-[#00E676]' : 'text-[#FFC107]'}`}>
                            {global_analytics.win_rate}%
                          </div>
                        </div>
                        <div>
                          <div className="text-white/40 text-xs font-mono mb-1">AVG LOSS RR</div>
                          <div className="text-xl font-bold font-mono text-white">
                            {global_analytics.avg_loss_rr !== null ? `${global_analytics.avg_loss_rr}R` : '—'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Setups Tab */}
            {activeTab === 'setups' && (
              <div data-testid="interactive-setups">
                {/* Setup Performance */}
                {isSectionEnabled('setup_performance') && setup_analytics.length > 0 && (
                  <div className="mb-8">
                    <h2 className="text-lg font-bold font-heading mb-4">Setup Performance</h2>
                    <BreakdownTable 
                      title="Performance by Setup" 
                      data={setup_analytics.filter(s => s.total_trades > 0)} 
                      columns={setupColumns} 
                    />
                  </div>
                )}

                {/* Asset Group Performance */}
                {isSectionEnabled('setup_performance') && asset_group_analytics.filter(g => g.total_trades > 0).length > 0 && (
                  <div className="mb-8">
                    <h2 className="text-lg font-bold font-heading mb-4">Asset Group Performance</h2>
                    <BreakdownTable 
                      title="Performance by Asset Group" 
                      data={asset_group_analytics.filter(g => g.total_trades > 0)} 
                      columns={groupColumns} 
                    />
                  </div>
                )}

                {setup_analytics.length === 0 && (
                  <div className="bg-[#121212] border border-white/10 rounded-sm p-8 text-center">
                    <p className="text-white/40">No setup data available</p>
                  </div>
                )}
              </div>
            )}

            {/* Breakdown Tab */}
            {activeTab === 'breakdown' && (
              <div data-testid="interactive-breakdown">
                {/* Timeframe Performance - only show if data exists */}
                {isSectionEnabled('timeframe_breakdown') && timeframe_breakdown?.length > 0 && (
                  <div className="mb-8">
                    <h2 className="text-lg font-bold font-heading mb-4">Timeframe Performance</h2>
                    <BreakdownTable 
                      title="Performance by Timeframe" 
                      data={timeframe_breakdown} 
                      columns={timeframeColumns} 
                    />
                  </div>
                )}

                {/* Planned Trades - only show if real data exists */}
                {isSectionEnabled('planned_not_triggered') && planned_trades_analytics?.total_planned > 0 && (
                  <div className="mb-8">
                    <h2 className="text-lg font-bold font-heading mb-4 flex items-center gap-2">
                      <Prohibit size={20} weight="duotone" className="text-[#FFA726]" />
                      Planned Trades (Not Triggered)
                    </h2>
                    <div className="bg-[#121212] border border-white/10 rounded-sm p-5">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                          <div className="text-white/40 text-xs font-mono mb-1">TOTAL PLANNED</div>
                          <div className="text-2xl font-bold font-mono text-[#FFA726]">{planned_trades_analytics.total_planned}</div>
                        </div>
                        <div className="md:col-span-2">
                          <div className="text-white/40 text-xs font-mono mb-2">BY SETUP</div>
                          <div className="flex flex-wrap gap-2">
                            {planned_trades_analytics.by_setup?.slice(0, 5).map((item, idx) => (
                              <span key={idx} className="px-2 py-1 bg-white/5 border border-white/10 rounded text-sm text-white/80">
                                {item.setup}: <span className="text-[#FFA726]">{item.count}</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Partial Execution - only show if real data exists */}
                {isSectionEnabled('partial_execution') && partial_execution_analytics?.total_partial > 0 && (
                  <div className="mb-8">
                    <h2 className="text-lg font-bold font-heading mb-4 flex items-center gap-2">
                      <Percent size={20} weight="duotone" className="text-white/60" />
                      Partial Execution
                    </h2>
                    <div className="bg-[#121212] border border-white/10 rounded-sm p-5">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <div className="text-white/40 text-xs font-mono mb-1">PARTIAL CLOSES</div>
                          <div className="text-xl font-bold font-mono text-white">{partial_execution_analytics.total_partial}</div>
                        </div>
                        <div>
                          <div className="text-white/40 text-xs font-mono mb-1">WINS</div>
                          <div className="text-xl font-bold font-mono text-[#00E676]">{partial_execution_analytics.win_count}</div>
                        </div>
                        <div>
                          <div className="text-white/40 text-xs font-mono mb-1">LOSSES</div>
                          <div className="text-xl font-bold font-mono text-[#FF3B30]">{partial_execution_analytics.loss_count}</div>
                        </div>
                        <div>
                          <div className="text-white/40 text-xs font-mono mb-1">WIN RATE</div>
                          <div className={`text-xl font-bold font-mono ${partial_execution_analytics.win_rate >= 50 ? 'text-[#00E676]' : 'text-[#FFC107]'}`}>
                            {partial_execution_analytics.win_rate}%
                          </div>
                        </div>
                      </div>
                      {partial_execution_analytics.by_reason?.length > 0 && (
                        <div>
                          <div className="text-white/40 text-xs font-mono mb-2">BY REASON</div>
                          <div className="flex flex-wrap gap-2">
                            {partial_execution_analytics.by_reason.map((item, idx) => (
                              <span key={idx} className="px-2 py-1 bg-white/5 border border-white/10 rounded text-sm text-white/80">
                                {item.reason}: <span className="text-white">{item.count}</span> ({item.win_rate}%)
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Fallback if no breakdown data at all */}
                {(!timeframe_breakdown || timeframe_breakdown.length === 0) && 
                 (!planned_trades_analytics || planned_trades_analytics.total_planned === 0) && 
                 (!partial_execution_analytics || partial_execution_analytics.total_partial === 0) && (
                  <div className="bg-[#121212] border border-white/10 rounded-sm p-8 text-center">
                    <p className="text-white/40">No breakdown data available for this report.</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* CTA - Interactive Mode Only */}
        {mode === 'interactive' && (
          <div className="flex justify-center mt-12 mb-10">
            <a
              href="/signup"
              className="px-6 py-3 bg-[#00E676] text-black font-semibold rounded-md hover:opacity-90 transition"
              data-testid="share-cta-signup"
            >
              Start tracking your own data
            </a>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="flex items-center justify-center gap-2 text-sm text-white/40">
            <img
              src={LOGO_PUREBRAIN}
              alt="GENI"
              className="h-5 w-auto object-contain opacity-90"
            />
            <span>
              Powered by <span className="text-white font-medium">GENI</span>
            </span>
          </div>
        </div>
      </main>
    </div>
  );
}
